import * as React from 'react';
import { useDatasheetController } from "./datasheet/main";
import { useFieldsController } from "./fields";
import { useDatasheetPrintController } from '../../../controllers/datasheet-print';
import { DatasheetLocalGetDto } from '../types/datasheet';
import { PRINTABLE_EDITOR_DATASHEET_ELEMENT_ID, PRINTABLE_DATASHEET_FIELD_ID } from '../../datasheet-printing-settings';
import { generateId } from '@common/utils/common';
import { loadDatasheetFormat, getNotLoadedDatasheetComponents } from "@modules/datasheets/services/datasheet";
import { DatasheetFormatManagementSubmitLoadParams, DatasheetFormatManagementSubmitParams } from "@modules/datasheets/components/datasheet-format-management/datasheet-format-management.types";
import { executeUI, updateCurrentDatasheetTab, closeCurrentTab } from "@common/utils/ui-bridge";
import { loadFormatDefaultsSettings } from '@modules/datasheets/services/formats';
import { convertApiDefaultsToUIFormats } from '@modules/datasheets/utils/datasheet-format-converter';
import { trDts } from '@modules/datasheets/utils/i18n';

const DEFAULT_GRID_SETTINGS = {
  horizontalGrid: 30,
  verticalGrid: 30,
  showGrid: true,
  showPageBreaks: false,
  snapToGrid: true,
  showEditTabOrder: false,
  showMargins: false,
  marginWidth: 400,
  marginHeight: 300,
};

export type EditorProps = { context: number; formatId: number; tableFieldId?: number; tableContext?: number };


export const useEditorController = ({ context, formatId, tableFieldId, tableContext }: EditorProps) => {
  const [activeContext, setActiveContext] = React.useState(context);
  const [activeTableContext, setActiveTableContext] = React.useState(tableContext);
  const [currentFormatId, setCurrentFormatId] = React.useState(formatId);
  const [formatReloadKey, setFormatReloadKey] = React.useState(0);
  const [datasheet, setDatasheet] = React.useState<DatasheetLocalGetDto>({
    id: 0,
    name: "",
    tabs: [
      {
        id: 1,
        name: "One",
        fields: [],
        texts: [],
      },
      {
        id: 2,
        name: "Two",
        fields: [],
        texts: [],
      },
      {
        id: 3,
        name: "Three",
        fields: [],
        texts: [],
      },
    ],
    gridSettings: DEFAULT_GRID_SETTINGS,
  });
  const loadedDefaultsRef = React.useRef<{ defaultFieldComponentSettings?: any; defaultTextComponentSettings?: any }>({});
  const closingViaSaveAsRef = React.useRef(false);
  // Add a state to track when defaults are loaded
  const [defaultsLoaded, setDefaultsLoaded] = React.useState(false);
  
  // Update currentFormatId when formatId prop changes
  React.useEffect(() => {
    setCurrentFormatId(formatId);
  }, [formatId]);
  React.useEffect(() => {
    setActiveContext(context);
    setActiveTableContext(tableContext);
  }, [context, tableContext]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        /**
         * For table datasheets (TDS):
         * - The editor tab is still opened with context 1/2 (IDS/PDS) so that
         *   folder trees, labels, etc. behave consistently.
         * - However, the backend `loadDSFormat` API expects `context = 8`
         *   when loading a table datasheet format.
         * - Other APIs (loadFormatDefaultsSettings, saveFormatDefaults, etc.) use the original context (1/2).
         *
         * We treat any editor opened with a non-zero tableFieldId as a
         * table datasheet editor and override the API context accordingly.
         */
        const isTableDatasheet = tableFieldId != null && tableFieldId !== 0;
        // Only use context 8 for loadDSFormat API
        const loadFormatApiContext = isTableDatasheet ? 8 : activeContext;

        // Load format and defaults in parallel
        const [formatData, defaultsData] = await Promise.all([
          loadDatasheetFormat({
            formatId: currentFormatId,
            context: loadFormatApiContext,
            tableContext: activeTableContext,
            tabId: 1,
          }),
          loadFormatDefaultsSettings({
            formatId: currentFormatId,
            context: activeContext, // Use original context, not 8
            tds: isTableDatasheet,
          }).catch((e) => {
            // If defaults don't exist or fail, use defaults
            console.warn("Failed to load format defaults, using defaults", e);
            return null;
          }),
        ]);

        if (cancelled) {
          return;
        }

        const format = formatData?.format;
        if (!format) {
          return;
        }

        // Convert API defaults to UI formats
        const { defaultFieldComponentSettings, defaultTextComponentSettings, gridSettings } = 
          convertApiDefaultsToUIFormats(defaultsData?.data || {});
        
        // Store defaults in ref to apply when controller is ready
        loadedDefaultsRef.current = {
          defaultFieldComponentSettings,
          defaultTextComponentSettings,
        };
        setDefaultsLoaded(true); // Trigger the application effect
        
        // Update datasheet with format and grid settings
        const formatName = format.name || '';

        // Mirror GWT behavior: if no tabs are defined on the format, create three default tabs
        const tabs = Array.isArray(format.tabsData) && format.tabsData.length > 0
          ? format.tabsData.map((t: any, idx: number) => ({
              id: t.id ?? idx + 1,
              name: t.name || `Tab ${t.id ?? idx + 1}`,
              texts: t.texts || [],
              fields: t.fields || [],
            }))
          : [
              { id: 1, name: "One",   fields: [], texts: [] },
              { id: 2, name: "Two",   fields: [], texts: [] },
              { id: 3, name: "Three", fields: [], texts: [] },
            ];

        setDatasheet((prev) => ({
          ...prev,
          id: format.id ?? prev.id,
          name: formatName || prev.name,
          tabs,
          gridSettings,
        }));
        setFormatReloadKey((prev) => prev + 1);
        
        // Update tab title and container ID with new format info
        if (formatName && format.id) {
          updateCurrentDatasheetTab(format.id, activeContext, formatName);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load datasheet format", e);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeContext, activeTableContext, currentFormatId, tableFieldId]);

  const fieldsController = useFieldsController();
  const printableDatasheetHolderId = React.useMemo(() => `${PRINTABLE_EDITOR_DATASHEET_ELEMENT_ID}-${generateId()}`, []);

  const datasheetPrintController = useDatasheetPrintController({
    printableDatasheetHolderId: printableDatasheetHolderId,
    printableDatasheetFieldId: PRINTABLE_DATASHEET_FIELD_ID
  });

  // Handler for save as completion - closes current tab and opens new tab with new format (GWT parity)
  const handleSaveAsComplete = React.useCallback(async (
    newFormatId: number, 
    formatName?: string, 
    tableFieldIdFromSave?: number | null
  ) => {
    try {
      closingViaSaveAsRef.current = true;
      delete (window as any).onDatasheetDesignTabClose;
      closeCurrentTab();
      // Then open new tab with saved format (matches GWT's addEditIDSTab behavior)
      // Use tableFieldId from Save As if provided (for TDS formats), otherwise use current tableFieldId
      const newTableFieldId = tableFieldIdFromSave != null && tableFieldIdFromSave !== 0 
        ? tableFieldIdFromSave 
        : null;
      const newTableContext = newTableFieldId != null && newTableFieldId !== 0 
        ? activeTableContext 
        : undefined;
      
      await executeUI(
        "DATASHEETS",
        "EDIT",
        formatName || "Datasheet",
        activeContext,
        newFormatId,
        newTableFieldId,
        newTableContext
      );
    } catch (err) {
      console.error("Failed to open new tab after save as", err);
    }
  }, [activeContext, activeTableContext, tableFieldId]);

  const loadTabFormat = React.useCallback(
    async (tabId: number) => {
      // Use loose equality so we find the tab whether id is string or number
      const tab = datasheet.tabs?.find((t) => t.id == tabId);
      if (tab?.fields != null && Array.isArray(tab.fields) && tab.fields.length > 0) {
        return;
      }
      try {
        const isTableDatasheet = tableFieldId != null && tableFieldId !== 0;
        const response = await getNotLoadedDatasheetComponents({
          formatId: currentFormatId,
          tabs: [tabId],
          ...(isTableDatasheet ? { isTDS: true } : {}),
        });
        const format = (response as any)?.tabsData != null ? response : null;
        if (!format?.tabsData?.length) {
          return;
        }
        setDatasheet((prev) => {
          const updatedTabs = prev.tabs.map((t) => {
            const loaded = format.tabsData.find((tt: any) => (tt.id ?? 0) == t.id);
            if (!loaded || t.id != tabId) return t;
            return {
              ...t,
              fields: loaded.fields ?? t.fields ?? [],
              texts: loaded.texts ?? t.texts ?? [],
            };
          });
          return { ...prev, tabs: updatedTabs };
        });
      } catch (err) {
        console.error("Failed to load tab format", tabId, err);
      }
    },
    [currentFormatId, datasheet.tabs, tableFieldId]
  );

  const editorController = useDatasheetController({
    selectedFieldsInFieldPicker: fieldsController.selectedFieldsInFieldPicker,
    onSelectedFieldsInFieldPickerChange: fieldsController.handleSelectedFieldsInFieldPickerChange,
    datasheet,
    onDatasheetChange: setDatasheet,
    formatId: currentFormatId,
    formatReloadKey,
    onReloadFormat: handleSaveAsComplete,
    tableFieldId,
    loadTabFormat,
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

  const markUnsaved = React.useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const markSaved = React.useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  const handleDatasheetElementsDrop = React.useCallback(
    (params: Parameters<typeof editorController.handleDatasheetElementsDrop>[0]) => {
      markUnsaved();
      return editorController.handleDatasheetElementsDrop(params);
    },
    [editorController.handleDatasheetElementsDrop, markUnsaved]
  );

  const handleDatasheetTextElementsDrop = React.useCallback(
    () => {
      markUnsaved();
      return editorController.handleDatasheetTextElementsDrop();
    },
    [editorController.handleDatasheetTextElementsDrop, markUnsaved]
  );

  const handleDatasheetElementChange = React.useCallback(
    (
      id: Parameters<typeof editorController.handleDatasheetElementChange>[0],
      data: Parameters<typeof editorController.handleDatasheetElementChange>[1],
    ) => {
      markUnsaved();
      return editorController.handleDatasheetElementChange(id, data);
    },
    [editorController.handleDatasheetElementChange, markUnsaved]
  );

  const handleSaveSubmit = React.useCallback(
    async (ctx: number) => {
      await editorController.handleSaveSubmit(ctx);
      markSaved();
    },
    [editorController.handleSaveSubmit, markSaved]
  );

  const handleSaveAsSubmit = React.useCallback(
    async (payload: any, ctx: number, isTdsFromEditor: boolean = false) => {
      await editorController.handleSaveAsSubmit(payload, ctx, isTdsFromEditor);
      markSaved();
    },
    [editorController.handleSaveAsSubmit, markSaved]
  );

  React.useEffect(() => {
    if (typeof window === "undefined" || closingViaSaveAsRef.current) {
      return;
    }

    const win = window as any;

    const handler = () => {
      return new Promise<{ hasUnsavedChanges: boolean }>((resolve) => {
        if (!hasUnsavedChanges) {
          resolve({ hasUnsavedChanges: false });
          return;
        }

        if (!editorController.showInfoMessage) {
          resolve({ hasUnsavedChanges: false });
          return;
        }

        editorController.showInfoMessage(trDts("unsavedChangesMessage"), {
          size: "medium",
          confirmText: trDts("Save"),
          cancelText: trDts("Cancel"),
          hideMessageBox: () => {
            editorController.closeMessage?.();
            resolve({ hasUnsavedChanges: true });
          },
          additionalText: trDts("dontSave"),
          onAdditional: () => {
            editorController.closeMessage?.();
            if (typeof (window as any).closeDatasheetDesignTab === "function") {
              (window as any).closeDatasheetDesignTab();
            }
            resolve({ hasUnsavedChanges: false });
          },
          onCustomOk: async () => {
            try {
              editorController.closeMessage?.();
              await handleSaveSubmit(activeContext);
              if (typeof (window as any).closeDatasheetDesignTab === "function") {
                (window as any).closeDatasheetDesignTab();
              }
              resolve({ hasUnsavedChanges: false });
            } catch (e) {
              console.error("Failed to save datasheet format on tab close", e);
              resolve({ hasUnsavedChanges: true });
            }
          },
        });
      });
    };

    win.onDatasheetDesignTabClose = handler;

    return () => {
      if (win.onDatasheetDesignTabClose === handler) {
        delete win.onDatasheetDesignTabClose;
      }
    };
  }, [activeContext, editorController.showInfoMessage, editorController.closeMessage, hasUnsavedChanges, handleSaveSubmit]);

  React.useEffect(() => {
    if (defaultsLoaded && 
        loadedDefaultsRef.current.defaultFieldComponentSettings && 
        loadedDefaultsRef.current.defaultTextComponentSettings &&
        editorController.setDefaultFieldComponentSettings && 
        editorController.setDefaultTextComponentSettings) {
      editorController.setDefaultFieldComponentSettings(loadedDefaultsRef.current.defaultFieldComponentSettings);
      editorController.setDefaultTextComponentSettings(loadedDefaultsRef.current.defaultTextComponentSettings);
      loadedDefaultsRef.current = {};
      setDefaultsLoaded(false); 
    }
  }, [defaultsLoaded, editorController.setDefaultFieldComponentSettings, editorController.setDefaultTextComponentSettings]);


  const handleDatasheetFormatLoadSubmit = React.useCallback(
    async (params: DatasheetFormatManagementSubmitParams) => {
      // Only handle load mode submissions
      const loadParams = params as DatasheetFormatManagementSubmitLoadParams;
      if (!loadParams?.format?.id) {
        return;
      }

      try {
        // Determine if the loaded format is a table datasheet
        // If tableContext or tableFieldId is provided, it's a table datasheet
        const isTableDatasheet = loadParams.tableContext != null || (loadParams.tableFieldId != null && loadParams.tableFieldId !== 0);
        
        // Only use context=8 for loadDSFormat API, not for executeUI
        // executeUI should use the original context (1/2) for UI consistency
        const apiTableContext = isTableDatasheet ? (loadParams.tableContext ?? activeTableContext) : undefined;
        
        // Reset tableFieldId to 0 if loading a non-table datasheet, otherwise use from load params
        const tableField = isTableDatasheet 
          ? (loadParams.tableFieldId ?? tableFieldId ?? 0)
          : 0; // Reset to 0 for non-table datasheets

        // Close immediately to avoid flicker while the format loads.
        editorController.closeDatasheetFormatManagement?.();

        await executeUI(
          "DATASHEETS",
          "EDIT",
          loadParams.format.name || "Datasheet",
          loadParams.context ?? activeContext, // Use original context for executeUI, not 8
          Number(loadParams.format.id),
          tableField,
          apiTableContext
        );
      } catch (err) {
        console.error("Failed to load datasheet via Load dialog", err);
      }
    },
    [activeContext, activeTableContext, tableFieldId, editorController.closeDatasheetFormatManagement]
  );

  return {
    activeContext,
    activeTableContext,
    currentFormatId,
    datasheet,
    printableDatasheetHolderId,
    handleDatasheetFormatLoadSubmit,
    ...editorController,
    handleDatasheetElementsDrop,
    handleDatasheetTextElementsDrop,
    handleDatasheetElementChange,
    handleSaveSubmit,
    handleSaveAsSubmit,
    hasUnsavedChanges,
    ...fieldsController,
    ...datasheetPrintController,
  };
};